import React, { useMemo, useState, useEffect } from 'react';
import { Card, Form, Input, Button, Table, InputNumber, App, DatePicker, Select, Switch, Alert, Space } from 'antd';
import { ArrowLeftOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { validateSupplierForPo } from '@/services/supplierLifecycle.service';
import dayjs from 'dayjs';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/services/api.service';
import { purchaseOrderService, type PurchaseOrderDTO, type PurchaseOrderItemDTO } from '@/services/purchaseOrder.service';
import { useCurrency } from '@/contexts/CurrencyContext';
import { PURCHASE_ORDER_PAYMENT_TERMS_OPTIONS } from '@/constants/purchaseOrderPaymentTerms';
import {
  PO_TYPE_OPTIONS,
  PO_TYPE_STANDARD,
  isEnvelopePoType,
} from '@/constants/purchaseOrderTypes';
import { usePaymentTermsForSelection } from '@/hooks/usePaymentTerms';
import { uiPlainCurrencyLabel } from '@/constants/uaDirhamDisplay';
import { useItems } from '@/hooks/useInventory';
import { useProjectsForSelection } from '@/hooks/useProject';
import type { Item } from '@/services/inventory.service';
import { ItemSelect, formatCatalogItemDescription } from '@/components/inventory/ItemSelect';
import { lineSpecificationsExpandable } from '@/components/procurement/LineSpecifications';
import {
  CriticalItemTag,
  getCriticalItemRowClassName,
  isItemCritical,
} from '@/components/inventory/criticalItemUi';
import PoMilestoneScheduleEditor, { type MilestoneRowData } from '@/components/procurement/PoMilestoneScheduleEditor';
import PoOverheadEditor, { type PoOverheadDraft } from '@/components/procurement/PoOverheadEditor';
import { CostControlLineCell } from '@/components/project/CostControlLineCell';
import type { PoMilestoneInput } from '@/services/poMilestone.service';
import { PO_PAYMENT_TERM_MILESTONE, PO_PAYMENT_TERM_STANDARD } from '@/constants/poMilestoneTypes';
import { useAuthStore } from '@/store/authStore';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { useApplyUserDefaultDepartment } from '@/utils/userDefaultDepartment';

const { TextArea } = Input;

function unwrap<T>(res: any): T {
  const data = res?.data ?? res;
  return (data?.data ?? data) as T;
}

interface SupplierOption {
  id: string;
  supplier_code?: string;
  supplier_name: string;
  address_line1?: string;
  contact_person?: string;
  currency?: string;
  is_active?: boolean;
}

/** Tax type options: label shown, percentage and applicable set on selection */
const TAX_TYPE_OPTIONS = [
  { label: 'VAT 5%', value: 'VAT_5', vat_percentage: 5, vat_applicable: true },
  { label: 'VAT 0%', value: 'VAT_0', vat_percentage: 0, vat_applicable: true },
  { label: 'Exempt', value: 'EXEMPT', vat_percentage: 0, vat_applicable: false },
] as const;

function getTaxTypeValue(vat_percentage: number | undefined, vat_applicable: boolean | undefined): string {
  if (vat_applicable === false) return 'EXEMPT';
  if (vat_percentage === 0) return 'VAT_0';
  return 'VAT_5'; // default and 5%
}

const initialLine = {
  item_code: '',
  item_description: '',
  supplier_description: '',
  ordered_quantity: 1,
  uom: 'EA',
  project_id: '',
  wbs_node_id: null as string | null,
  cbs_code_id: null as string | null,
  cost_category: 'Material',
  unit_price: 0,
  discount_percentage: 0,
  vat_percentage: 5,
  vat_applicable: true,
  is_critical: false,
  specifications: '',
};

const PurchaseOrderCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { baseCurrency, allCurrencies, loading: currenciesLoading } = useCurrency();
  const [form] = Form.useForm();
  useApplyUserDefaultDepartment(form, true);
  const selectedCurrency = Form.useWatch('currency', form);
  const exchangeRate = Form.useWatch('exchange_rate', form);
  const poTypeWatch = Form.useWatch('po_type', form);
  const isEnvelope = isEnvelopePoType(poTypeWatch);
  const [supplierPoWarning, setSupplierPoWarning] = useState<string | null>(null);
  const [overheads, setOverheads] = useState<PoOverheadDraft[]>([]);
  const { user } = useAuthStore();
  const isSuperAdmin = !!user?.is_super_admin;
  const perms = user?.permissions || [];
  const canManageOverhead =
    isSuperAdmin ||
    perms.includes('procurement.overhead.manage') ||
    perms.includes('procurement.po.create') ||
    perms.includes('procurement.po.edit');
  const canViewOverhead =
    canManageOverhead ||
    perms.includes('procurement.overhead.view') ||
    perms.includes('procurement.po.view');


  const currencyOptions = useMemo(() => {
    const active = (allCurrencies || []).filter((c) => c.is_active);
    if (active.length > 0) {
      return active.map((c) => {
        const d = uiPlainCurrencyLabel(c.symbol, c.currency_code);
        const label =
          d === c.currency_code ? `${c.currency_code} (${c.currency_name})` : `${d} ${c.currency_code}`;
        return { value: c.currency_code, label };
      });
    }
    const base = baseCurrency?.currency_code ?? 'AED';
    return [{ value: base, label: baseCurrency?.currency_name ? `${base} (${baseCurrency.currency_name})` : base }];
  }, [allCurrencies, baseCurrency]);

  const { data: paymentTermsMaster = [] } = usePaymentTermsForSelection();
  const { data: projects = [] } = useProjectsForSelection();
  const projectOptions = useMemo(
    () => projects.map((p: { id: string; project_name?: string; project_number?: string }) => ({
      value: p.id,
      label: p.project_name ?? p.project_number ?? p.id,
    })),
    [projects]
  );
  const paymentTermsOptions = useMemo(() => {
    const seen = new Set<string>();
    const opts: { value: string; label: string }[] = [];
    const add = (value: string, label: string) => {
      const v = value.trim();
      if (!v || seen.has(v)) return;
      seen.add(v);
      opts.push({ value: v, label });
    };
    for (const t of paymentTermsMaster) {
      const value = (t.display_label || t.description || t.term_name).trim();
      add(value, t.term_name);
    }
    for (const o of PURCHASE_ORDER_PAYMENT_TERMS_OPTIONS) {
      add(o.value, o.label);
    }
    return opts;
  }, [paymentTermsMaster]);

  const [lines, setLines] = useState<Partial<PurchaseOrderItemDTO>[]>([{ ...initialLine }]);
  const [paymentTermType, setPaymentTermType] = useState(PO_PAYMENT_TERM_STANDARD);
  const [milestoneRows, setMilestoneRows] = useState<MilestoneRowData[]>([]);

  const estimatedGrandTotal = useMemo(() => {
    const subtotal = lines.reduce(
      (s, l) => s + (Number(l.ordered_quantity) || 0) * (Number(l.unit_price) || 0),
      0
    );
    const disc = lines.reduce((s, l) => {
      const lineTotal = (Number(l.ordered_quantity) || 0) * (Number(l.unit_price) || 0);
      return s + (lineTotal * (Number(l.discount_percentage) || 0)) / 100;
    }, 0);
    const afterDisc = subtotal - disc;
    const vat = lines.reduce((s, l) => {
      const lineTotal = (Number(l.ordered_quantity) || 0) * (Number(l.unit_price) || 0);
      const lineDisc = (lineTotal * (Number(l.discount_percentage) || 0)) / 100;
      const net = lineTotal - lineDisc;
      return s + (l.vat_applicable !== false ? (net * (Number(l.vat_percentage) ?? 5)) / 100 : 0);
    }, 0);
    return Math.round((afterDisc + vat) * 100) / 100;
  }, [lines]);

  const baseCode = baseCurrency?.currency_code ?? 'AED';
  const isForeignCurrency = !!selectedCurrency && selectedCurrency !== baseCode;
  const baseGrandTotalPreview = useMemo(() => {
    if (!isForeignCurrency) return estimatedGrandTotal;
    const rate = Number(exchangeRate) || 0;
    if (rate <= 0) return estimatedGrandTotal;
    return Math.round(estimatedGrandTotal * rate * 100) / 100;
  }, [estimatedGrandTotal, exchangeRate, isForeignCurrency]);

  useEffect(() => {
    if (!selectedCurrency || selectedCurrency === baseCode) {
      form.setFieldValue('exchange_rate', 1);
      return;
    }
    const row = allCurrencies.find((c) => c.currency_code === selectedCurrency);
    if (row?.exchange_rate) {
      form.setFieldValue('exchange_rate', Number(row.exchange_rate));
    }
  }, [selectedCurrency, baseCode, allCurrencies, form]);

  const { data: suppliersRaw } = useQuery({
    queryKey: ['procurement-suppliers'],
    queryFn: async () => {
      const res = await api.get('/procurement/suppliers');
      return unwrap<SupplierOption[]>(res) ?? [];
    },
  });
  const suppliers: SupplierOption[] = useMemo(() => {
    const list = Array.isArray(suppliersRaw) ? suppliersRaw : [];
    return list.filter((s) => s.is_active !== false);
  }, [suppliersRaw]);
  const supplierOptions = useMemo(
    () =>
      suppliers.map((s) => ({
        value: s.id,
        label: s.supplier_code ? `${s.supplier_code} - ${s.supplier_name}` : s.supplier_name,
      })),
    [suppliers]
  );
  const { data: inventoryItems = [], isLoading: inventoryItemsLoading } = useItems({ is_active: true });

  const createMutation = useMutation({
    mutationFn: (payload: {
      po: Record<string, unknown>;
      items: Partial<PurchaseOrderItemDTO>[];
      milestones?: PoMilestoneInput[];
      overheads?: PoOverheadDraft[];
    }) => purchaseOrderService.create(payload),
    onSuccess: (created: PurchaseOrderDTO) => {
      message.success('Purchase order created');
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      navigate(`/scm/procurement/purchase-orders/${created.id}`);
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message || 'Create failed');
    },
  });

  const addLine = () => {
    setLines((prev) => [...prev, { ...initialLine }]);
  };

  const removeLine = (index: number) => {
    if (lines.length <= 1) return;
    setLines((prev) => prev.filter((_, i) => i !== index));
  };

  const updateLine = (index: number, field: string, value: unknown) => {
    setLines((prev) => {
      const next = [...prev];
      const row = next[index] as any;
      if (!row) return next;
      if (field === 'item_description') {
        const current = String(row.item_description ?? '');
        const supplier = String(row.supplier_description ?? '');
        row.item_description = value;
        if (!supplier || supplier === current) row.supplier_description = value;
      } else {
        row[field] = value;
      }
      return next;
    });
  };

  const onSelectInventoryItem = (index: number, itemId: string) => {
    const item = inventoryItems.find((i: Item) => i.id === itemId);
    if (!item) return;
    setLines((prev) => {
      const next = [...prev];
      const row = next[index];
      if (row) {
        (row as any).item_code = item.item_code ?? '';
        const description = formatCatalogItemDescription(item);
        const supplier = String((row as any).supplier_description ?? '');
        const current = String((row as any).item_description ?? '');
        (row as any).item_description = description;
        if (!supplier || supplier === current) (row as any).supplier_description = description;
        (row as any).uom = item.purchase_uom || item.base_uom || 'EA';
        (row as any).unit_price = Number(item.last_purchase_price) || Number(item.standard_cost) || 0;
        const pct = item.vat_percentage != null ? Number(item.vat_percentage) : 5;
        const appl = item.vat_applicable ?? true;
        (row as any).vat_percentage = pct;
        (row as any).vat_applicable = appl;
        (row as any).is_critical = isItemCritical(item);
        if (!String((row as any).specifications ?? '').trim() && item.specifications?.trim()) {
          (row as any).specifications = item.specifications;
        }
      }
      return next;
    });
  };

  const onFinish = () => {
    const values = form.getFieldsValue();
    const supplierId = values.supplier_id;
    const supplier = suppliers.find((s) => s.id === supplierId);
    if (!supplierId || !supplier) {
      message.error('Please select a supplier');
      return;
    }
    const distinctLineProjects = [
      ...new Set(
        lines
          .map((l) => (l as { project_id?: string }).project_id)
          .filter((id): id is string => Boolean(id))
      ),
    ];
    const validLines = lines
      .map((l, i) => ({
        line_number: i + 1,
        item_code: l.item_code || undefined,
        item_description: String(l.item_description ?? '').trim(),
        supplier_description: String((l as any).supplier_description ?? l.item_description ?? '').trim(),
        ordered_quantity: Number(l.ordered_quantity) || 0,
        uom: l.uom || 'EA',
        unit_price: Number(l.unit_price) ?? 0,
        discount_percentage: Number(l.discount_percentage) ?? 0,
        vat_percentage: Number(l.vat_percentage) ?? 5,
        vat_applicable: l.vat_applicable !== false,
        project_id:
          (l as { project_id?: string }).project_id ||
          (distinctLineProjects.length ? undefined : values.project_id) ||
          undefined,
        wbs_node_id: l.wbs_node_id || null,
        cbs_code_id: l.cbs_code_id || null,
        cost_category: l.cost_category || 'Material',
        specifications: String(l.specifications ?? '').trim() || undefined,
      }))
      .filter((l) => l.item_description && l.ordered_quantity > 0 && l.unit_price >= 0);
    if (validLines.length === 0) {
      message.error('Add at least one line with description, quantity and unit price');
      return;
    }
    const poPayload = {
      supplier_id: supplierId,
      supplier_name: supplier.supplier_name,
      supplier_address: supplier.address_line1 ?? undefined,
      supplier_contact: supplier.contact_person ?? undefined,
      po_date: values.po_date ? values.po_date.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
      po_type: values.po_type || PO_TYPE_STANDARD,
      delivery_date: values.delivery_date ? values.delivery_date.format('YYYY-MM-DD') : undefined,
      delivery_to: values.delivery_to,
      delivery_address: values.delivery_address,
      currency: values.currency ?? baseCurrency?.currency_code ?? 'AED',
      exchange_rate: Number(values.exchange_rate ?? 1),
      payment_terms: values.payment_terms,
      project_id:
        distinctLineProjects.length === 1
          ? distinctLineProjects[0]
          : distinctLineProjects.length > 1
            ? null
            : values.project_id || undefined,
      agreement_start_date: values.agreement_start_date
        ? values.agreement_start_date.format('YYYY-MM-DD')
        : undefined,
      agreement_end_date: values.agreement_end_date
        ? values.agreement_end_date.format('YYYY-MM-DD')
        : undefined,
      max_value: values.max_value != null ? Number(values.max_value) : undefined,
      notes: values.notes,
      terms_and_conditions: values.terms_and_conditions,
      special_instructions: values.special_instructions,
      department_id: values.department_id,
      po_status: 'Draft',
      payment_term_type: paymentTermType,
    };
    const milestones =
      paymentTermType === PO_PAYMENT_TERM_MILESTONE
        ? milestoneRows.map((m, i) => ({
            milestone_number: i + 1,
            milestone_name: m.milestone_name,
            description: m.description,
            milestone_type: m.milestone_type,
            fulfillment_method: m.fulfillment_method,
            percentage: m.percentage,
            milestone_amount: m.milestone_amount,
            expected_date: m.expected_date,
            trigger_condition: m.trigger_condition,
            supporting_deliverable: m.supporting_deliverable,
            invoice_requirement: m.invoice_requirement,
            tax_applicable: m.tax_applicable,
            retention_applicable: m.retention_applicable,
            retention_percentage: m.retention_percentage,
            remarks: m.remarks,
          }))
        : undefined;
    if (paymentTermType === PO_PAYMENT_TERM_MILESTONE && (!milestones?.length || !milestones[0]?.milestone_name?.trim())) {
      message.error('Add at least one milestone with a name');
      return;
    }
    createMutation.mutate({
      po: poPayload,
      items: validLines,
      milestones,
      overheads: canManageOverhead
        ? overheads.map((oh, i) => ({
            line_number: i + 1,
            overhead_type: oh.overhead_type || 'Other',
            description: oh.description,
            remarks: oh.remarks,
            currency: oh.currency || poPayload.currency,
            exchange_rate: Number(oh.exchange_rate) || 1,
            original_amount: Number(oh.original_amount ?? oh.amount) || 0,
            amount: Number(oh.amount ?? oh.original_amount) || 0,
          }))
        : undefined,
    });
  };

  return (
    <div style={{ padding: 24 }}>
      <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/scm/procurement/purchase-orders')} style={{ marginBottom: 16 }}>
        Back
      </Button>
      <Card title="Create Purchase Order (Manual)">
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            po_date: dayjs(),
            po_type: PO_TYPE_STANDARD,
            currency: baseCurrency?.currency_code ?? 'AED',
            payment_terms: 'Net 30',
          }}
        >
          <Form.Item name="supplier_id" label="Supplier" rules={[{ required: true, message: 'Select supplier' }]}>
            <Select
              placeholder="Search supplier"
              showSearch
              optionFilterProp="label"
              filterOption={(input, opt) =>
                (opt?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
              }
              options={supplierOptions}
              onChange={async (supplierId: string) => {
                const s = suppliers.find((x) => x.id === supplierId);
                if (s?.currency) {
                  form.setFieldValue('currency', s.currency);
                }
                setSupplierPoWarning(null);
                try {
                  const v = await validateSupplierForPo(supplierId);
                  if (!v.allowed) {
                    setSupplierPoWarning(v.warning ?? 'Supplier cannot be used on PO');
                  } else if (v.warning) {
                    setSupplierPoWarning(v.warning);
                  }
                } catch {
                  /* backend will enforce on submit */
                }
              }}
            />
          </Form.Item>
          {supplierPoWarning ? <Alert type="warning" message={supplierPoWarning} showIcon style={{ marginBottom: 16 }} /> : null}
          <Form.Item
            name="po_type"
            label="PO type"
            rules={[{ required: true, message: 'Select PO type' }]}
            extra={
              isEnvelope
                ? 'Envelope orders hold agreed pricing; create releases/call-offs for delivery after approval.'
                : undefined
            }
          >
            <Select
              options={PO_TYPE_OPTIONS.map((o) => ({
                value: o.value,
                label: o.label,
                title: o.description,
              }))}
            />
          </Form.Item>
          <Form.Item name="po_date" label="PO Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="delivery_date"
            label={isEnvelope ? 'Expected delivery date (optional)' : 'Expected delivery date'}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          {isEnvelope ? (
            <>
              <Form.Item name="agreement_start_date" label="Agreement start">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="agreement_end_date" label="Agreement end">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="max_value" label="Max value (optional)">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </>
          ) : null}
          <Form.Item name="delivery_to" label="Delivery to">
            <Input />
          </Form.Item>
          <Form.Item name="delivery_address" label="Delivery address">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="currency" label="Currency" rules={[{ required: true, message: 'Select currency' }]}>
            <Select
              showSearch
              optionFilterProp="label"
              loading={currenciesLoading}
              options={currencyOptions}
            />
          </Form.Item>
          {isForeignCurrency && (
            <>
              <Form.Item
                name="exchange_rate"
                label={`Exchange rate (1 ${selectedCurrency} = ? ${baseCode})`}
                rules={[{ required: true, message: 'Enter exchange rate' }]}
                initialValue={1}
              >
                <InputNumber min={0.000001} step={0.0001} style={{ width: '100%' }} />
              </Form.Item>
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message={`Estimated total: ${estimatedGrandTotal.toLocaleString()} ${selectedCurrency} ≈ ${baseGrandTotalPreview.toLocaleString()} ${baseCode}`}
              />
            </>
          )}
          <Form.Item name="payment_terms" label="Payment terms">
            <Select
              showSearch
              optionFilterProp="label"
              options={paymentTermsOptions}
            />
          </Form.Item>
          <Form.Item label="Payment term type">
            <Switch
              checked={paymentTermType === PO_PAYMENT_TERM_MILESTONE}
              checkedChildren="Milestone-based"
              unCheckedChildren="Standard"
              onChange={(c) => {
                setPaymentTermType(c ? PO_PAYMENT_TERM_MILESTONE : PO_PAYMENT_TERM_STANDARD);
                if (c && milestoneRows.length === 0) {
                  setMilestoneRows([
                    {
                      key: 'ms-1',
                      milestone_number: 1,
                      milestone_name: '',
                      milestone_type: 'Material Delivery',
                      fulfillment_method: 'GRN',
                      percentage: 100,
                      milestone_amount: estimatedGrandTotal,
                      tax_applicable: true,
                      retention_applicable: false,
                      retention_percentage: 0,
                    },
                  ]);
                }
              }}
            />
          </Form.Item>
          <PoMilestoneScheduleEditor
            rows={milestoneRows}
            onChange={setMilestoneRows}
            grandTotal={estimatedGrandTotal}
            paymentTermType={paymentTermType}
          />
          {lines.some((line) => Boolean((line as { project_id?: string }).project_id)) ? null : (
            <Form.Item name="project_id" label="Project">
              <Select allowClear showSearch optionFilterProp="label" placeholder="Select project" options={projectOptions} />
            </Form.Item>
          )}
          <Form.Item
            name="department_id"
            label="Department"
            rules={[{ required: true, message: 'Department is required' }]}
          >
            <DepartmentSelect />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="terms_and_conditions" label="Terms and conditions">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="special_instructions" label="Special instructions">
            <TextArea rows={2} />
          </Form.Item>

          <h4>Line items (at least one required)</h4>
          <Table
            size="small"
            rowKey={(_, i) => String(i)}
            pagination={false}
            dataSource={lines}
            scroll={{ x: 1680 }}
            rowClassName={(r) => getCriticalItemRowClassName(r as { is_critical?: boolean })}
            expandable={lineSpecificationsExpandable<(typeof lines)[number]>({
              getValue: (r) => r.specifications,
              onChange: (r, v) => updateLine(lines.indexOf(r), 'specifications', v),
            })}
            columns={[
              { title: '#', width: 44, fixed: 'left', render: (_, __, i) => i + 1 },
              {
                title: 'Item (from inventory)',
                width: 240,
                render: (_, __, i) => (
                  <ItemSelect
                    items={inventoryItems}
                    autoLoad={false}
                    loading={inventoryItemsLoading}
                    placeholder="Select item or enter manually below"
                    style={{ width: '100%', minWidth: 200 }}
                    value={undefined}
                    onChange={(v) => v && onSelectInventoryItem(i, String(v))}
                  />
                ),
              },
              {
                title: 'Item code',
                width: 160,
                render: (_, __, i) => (
                  <Space size={6} wrap>
                    <Input
                      value={lines[i]?.item_code}
                      onChange={(e) => updateLine(i, 'item_code', e.target.value)}
                      placeholder="Optional"
                      style={{ width: 110 }}
                    />
                    <CriticalItemTag critical={isItemCritical(lines[i] as { is_critical?: boolean })} />
                  </Space>
                ),
              },
              {
                title: 'Description',
                width: 200,
                render: (_, __, i) => (
                  <Input
                    value={lines[i]?.item_description}
                    onChange={(e) => updateLine(i, 'item_description', e.target.value)}
                    placeholder="Required"
                  />
                ),
              },
              {
                title: 'Supplier Description',
                width: 200,
                render: (_, __, i) => (
                  <Input
                    value={(lines[i] as any)?.supplier_description ?? lines[i]?.item_description}
                    onChange={(e) => updateLine(i, 'supplier_description', e.target.value)}
                    placeholder="Shown on the purchase order print"
                  />
                ),
              },
              {
                title: 'Qty',
                width: 100,
                render: (_, __, i) => (
                  <InputNumber
                    min={0.0001}
                    value={lines[i]?.ordered_quantity}
                    onChange={(v) => updateLine(i, 'ordered_quantity', v)}
                    style={{ width: '100%' }}
                  />
                ),
              },
              {
                title: 'UOM',
                width: 80,
                render: (_, __, i) => (
                  <Input value={lines[i]?.uom} onChange={(e) => updateLine(i, 'uom', e.target.value)} />
                ),
              },
              {
                title: 'Unit price',
                width: 110,
                render: (_, __, i) => (
                  <InputNumber
                    min={0}
                    value={lines[i]?.unit_price}
                    onChange={(v) => updateLine(i, 'unit_price', v)}
                    style={{ width: '100%' }}
                  />
                ),
              },
              {
                title: 'Discount %',
                width: 100,
                render: (_, __, i) => (
                  <InputNumber
                    min={0}
                    max={100}
                    value={lines[i]?.discount_percentage}
                    onChange={(v) => updateLine(i, 'discount_percentage', v)}
                    style={{ width: '100%' }}
                  />
                ),
              },
              {
                title: 'Project',
                width: 200,
                render: (_, __, i) => (
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="Project"
                    style={{ width: '100%', minWidth: 160 }}
                    options={projectOptions}
                    value={(lines[i] as { project_id?: string })?.project_id || undefined}
                    onChange={(v) => updateLine(i, 'project_id', v)}
                  />
                ),
              },
              {
                title: 'Budget / WBS',
                width: 200,
                render: (_, __, i) => (
                  <CostControlLineCell
                    projectId={(lines[i] as { project_id?: string })?.project_id}
                    value={{
                      wbs_node_id: lines[i]?.wbs_node_id,
                      cbs_code_id: lines[i]?.cbs_code_id,
                      cost_category: lines[i]?.cost_category,
                    }}
                    requestedAmount={
                      Number(lines[i]?.ordered_quantity || 0) *
                      Number(lines[i]?.unit_price || 0) *
                      (1 - Number(lines[i]?.discount_percentage || 0) / 100)
                    }
                    onChange={(patch) => {
                      updateLine(i, 'wbs_node_id', patch.wbs_node_id ?? null);
                      updateLine(i, 'cbs_code_id', patch.cbs_code_id ?? null);
                      updateLine(i, 'cost_category', patch.cost_category ?? 'Material');
                    }}
                  />
                ),
              },
              {
                title: 'Tax type',
                width: 120,
                render: (_, __, i) => (
                  <Select
                    style={{ width: '100%' }}
                    placeholder="Tax type"
                    options={TAX_TYPE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                    value={getTaxTypeValue(lines[i]?.vat_percentage, lines[i]?.vat_applicable)}
                    onChange={(value) => {
                      const opt = TAX_TYPE_OPTIONS.find((o) => o.value === value);
                      if (opt) {
                        updateLine(i, 'vat_percentage', opt.vat_percentage);
                        updateLine(i, 'vat_applicable', opt.vat_applicable);
                      }
                    }}
                  />
                ),
              },
              {
                title: '',
                width: 50,
                render: (_, __, i) =>
                  lines.length > 1 ? (
                    <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => removeLine(i)} />
                  ) : null,
              },
            ]}
          />
          <Button type="dashed" onClick={addLine} icon={<PlusOutlined />} style={{ marginTop: 8 }}>
            Add line
          </Button>

          {canViewOverhead ? (
            <PoOverheadEditor
              overheads={overheads}
              onChange={setOverheads}
              currency={selectedCurrency || baseCurrency?.currency_code || 'AED'}
              readOnly={!canManageOverhead}
            />
          ) : null}

          <Form.Item style={{ marginTop: 24 }}>
            <Button type="primary" htmlType="submit" loading={createMutation.isPending}>
              Create PO
            </Button>
            <Button style={{ marginLeft: 8 }} onClick={() => navigate('/scm/procurement/purchase-orders')}>
              Cancel
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default PurchaseOrderCreatePage;
